import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { FiX, FiSearch } from 'react-icons/fi';

const ProductSelectorModal = ({ initialConfig, onSave, onClose }) => {
  const [config, setConfig] = useState(initialConfig);
  const [allProducts, setAllProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const products = await api.get('/products');
      setAllProducts(products);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const cats = await api.get('/categories');
      setCategories(cats);
    } catch (err) {
      console.error(err);
    }
  };

  const handleTypeChange = (type) => {
    setConfig({
      ...config,
      selectionType: type,
      productIds: type === 'manual' ? config.productIds : [],
      categoryId: type === 'category' ? config.categoryId : undefined
    });
  };

  const toggleProduct = (productId) => {
    let newIds = [...(config.productIds || [])];
    if (newIds.includes(productId)) {
      newIds = newIds.filter(id => id !== productId);
    } else {
      newIds.push(productId);
    }
    setConfig({ ...config, productIds: newIds });
  };

  const filteredProducts = allProducts.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-semibold">Configure Products for Section</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <FiX size={20} />
          </button>
        </div>
        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          <div>
            <label className="block text-sm font-medium mb-1">Selection Type</label>
            <select
              value={config.selectionType}
              onChange={(e) => handleTypeChange(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-2"
            >
              <option value="manual">Manual Selection</option>
              <option value="category">By Category</option>
              <option value="featured">Featured Products</option>
              <option value="bestseller">Best Sellers</option>
              <option value="new">New Arrivals</option>
            </select>
          </div>

          {config.selectionType === 'category' && (
            <div>
              <label className="block text-sm font-medium mb-1">Select Category</label>
              <select
                value={config.categoryId || ''}
                onChange={(e) => setConfig({ ...config, categoryId: e.target.value })}
                className="w-full border border-gray-300 rounded-lg p-2"
              >
                <option value="">-- Choose Category --</option>
                {categories.map(cat => (
                  <option key={cat._id} value={cat._id}>{cat.name}</option>
                ))}
              </select>
            </div>
          )}

          {config.selectionType === 'manual' && (
            <div>
              <label className="block text-sm font-medium mb-1">Search & Select Products</label>
              <div className="relative mb-2">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-2 pl-10"
                />
              </div>
              <div className="max-h-60 overflow-y-auto border rounded-lg p-2 space-y-1">
                {filteredProducts.map(product => (
                  <label key={product._id} className="flex items-center gap-2 p-2 hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={(config.productIds || []).includes(product._id)}
                      onChange={() => toggleProduct(product._id)}
                      className="w-4 h-4"
                    />
                    {product.images?.[0] && (
                      <img src={product.images[0]} alt="" className="w-10 h-10 object-cover rounded" />
                    )}
                    <span className="text-sm">{product.name} - Rs.{product.price}</span>
                  </label>
                ))}
                {filteredProducts.length === 0 && <p className="text-gray-500 text-sm">No products found</p>}
              </div>
              <p className="text-sm text-gray-500 mt-1">{(config.productIds || []).length} product(s) selected</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1">Max Products to Show (Limit)</label>
            <input
              type="number"
              min="1"
              max="50"
              value={config.limit || 12}
              onChange={(e) => setConfig({ ...config, limit: parseInt(e.target.value) || 12 })}
              className="w-full border border-gray-300 rounded-lg p-2"
            />
          </div>
        </div>
        <div className="border-t p-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={() => onSave(config)}
            className="px-4 py-2 bg-[#d60167] text-white rounded-lg hover:bg-[#b0005a]"
          >
            Save Configuration
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductSelectorModal;